/**
 * Ryan O'Dowd
 * 2022-04-13
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DraggableFlatList from 'react-native-draggable-flatlist';
import GameTemplate from '@containers/Practice/GameTemplate';
import Reference from '@containers/Practice/Reference';
import Verse from './Verse'; // @TODO: probably move this to /components/
import {
  View,
} from 'react-native';
import getStyles from './styles';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const OrderTheVerses = (props: OrderTheVersesProps) => {
  const styles = hooks.useStyles(getStyles);

  const [_questionComplete, setQuestionComplete] = useState(false); // @TODO: abstract this?
  const [_isCorrect, setIsCorrect] = useState(false);
  const [_isIncorrect, setIsIncorrect] = useState(false);
  const [_showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [_isDragging, setIsDragging] = useState(false);

  const [_contentSize, setContentSize] = useState(0);
  const [_gameContainerHeight, setGameContainerHeight] = useState(0);

  const notesRef = useRef(null);

  const numVersesInLesson = useMemo(() => {
    let difficultyNum;
    if (props.difficulty === 'easy') {
      difficultyNum = 3;
    } else if (props.difficulty === 'medium') {
      difficultyNum = 5;
    } else {
      // props.difficulty === 'hard'
      difficultyNum = 7;
    }

    return Math.min(difficultyNum, props.passage.end_verse_number - props.passage.start_verse_number + 1);
  }, [props.difficulty, props.passage.start_verse_number, props.passage.end_verse_number]);

  const startVerseNumber = useMemo(() => {
    return Math.max(props.passage.start_verse_number, props.passage.start_verse_number + Math.floor(Math.random() * (props.passage.end_verse_number - props.passage.start_verse_number - numVersesInLesson + 1 + 1))); // +1 to offset the OBO error with subtracting numVersesInLesson and +1 to offset the OBO error since Math.random is [0, 1) instead of [0, 1]
  }, [props.passage, numVersesInLesson]);

  const passageVerses = hooks.useGetVersesForPassages([props.passage])[props.passage.id];
  const versesToReorder = useMemo(() => {
    return [...Array(numVersesInLesson)].map((__, verseOffset) => {
      const verseNumber = startVerseNumber + verseOffset;
      const verseIndex = startVerseNumber - props.passage.start_verse_number + verseOffset;
      const verse = passageVerses[verseIndex];
      return {
        body: utilities.removeVerseFormatting(verse.verseText),
        number: verseNumber,
      };
    });
  }, [passageVerses, startVerseNumber, numVersesInLesson, props.passage.start_verse_number]);

  const [_verseOrderOverrides, setVerseOrderOverrides] = useState([]);

  useEffect(() => {
    setVerseOrderOverrides([...versesToReorder].sort(() => Math.random() - 0.5));
  }, [versesToReorder]);

  useLayoutEffect(() => { // @TODO: why is this necessary? can't it just be flexGrow: 1?
    notesRef.current?.measureInWindow((__x, __y, __width, height) => {
      setGameContainerHeight(height);
    });
  }, []);

  const _renderDraggableVerse = useCallback((d) => { // @TODO: does this need to be a useCallback
    const verseIndex = versesToReorder.map((v) => v.number).indexOf(d.item.number);
    // my best guess for why the null check is necessary is that _verseOrderOverrides hasn't been set yet (or rather, it's been set, but react hasn't saved it this re-render), so just return null until the update has been saved
    // TypeError: undefined is not an object (evaluating '_verseOrderOverrides[verseIndex].number')
    const isInCorrectIndex = _verseOrderOverrides[verseIndex]?.number === d.item.number;

    return (
      <Verse
        verseBody={d.item.body}
        answerSubmitted={_showCorrectAnswer}
        verseNumber={d.item.number}
        drag={d.drag}
        isInCorrectIndex={isInCorrectIndex}
        hasOverflow={_contentSize >= _gameContainerHeight}
      />
    );
  }, [_showCorrectAnswer, _verseOrderOverrides, versesToReorder, _contentSize, _gameContainerHeight]);

  return (
    <GameTemplate
      subHeaderHeight={props.subHeaderHeight}
      innerContainerStyles={styles.templateContainerStyles}
      questionComplete={_questionComplete}
      isIncorrect={_isIncorrect}
      isCorrect={_isCorrect}
      isCheckAnswerButtonDisabled={_isDragging}
      onPass={() => props.onPass(_isCorrect, numVersesInLesson)}
      scrollDisabled={true}
      onCheckAnswer={() => {
        props.onCheckAnswer();
        // @TODO: if failed, add to practice
        // @TODO: add score
        setQuestionComplete(true);
        // @TODO: set state for correct or not and display value to user
        // @TODO: compare _verseOrderOverrides and versesToReorder
        if (_verseOrderOverrides.map((v) => v.body).join('::') === versesToReorder.map((v) => v.body).join('::')) { // @NOTE: v.body is better than v.number because two verses could be the same text with different numbers
          // @TODO: play sound
          setIsCorrect(true);
        } else {
          // @TODO: play sound
          setIsIncorrect(true);
          setTimeout(() => {
            setVerseOrderOverrides(versesToReorder); // @TODO: wish i could animate these into the correct locations...
          }, 1500);
          // @TODO: animate the verses into correct position (make original out-of-order verse numbers red and correct ones accent)
        }
        setShowCorrectAnswer(true);
      }}
    >
      <Reference passage={props.passage.cloneWithChanges({startVerseNumber, endVerseNumber: startVerseNumber + numVersesInLesson - 1})} />
      <View
        ref={notesRef}
        style={styles.notesContainer}
      >
        <DraggableFlatList
          keyExtractor={(n) => n.number}
          style={styles.list}
          data={_verseOrderOverrides}
          renderItem={_renderDraggableVerse}
          showsVerticalScrollIndicator={false}
          activationDistance={1}// @NOTE: needs to be here for web to work
          onContentSizeChange={(__, height) => {
            setContentSize(height);
          }}
          keyboardDismissMode='on-drag'
          keyboardShouldPersistTaps='handled'
          scrollEnabled={true}
          onDragBegin={() => {
            setIsDragging(true);
          }}
          onDragEnd={({data}) => {
            // @NOTE: temporarily (component state) set note order so that the delay caused by writing to firebase, updating redux, and rerendering doesn't cause a flicker where the dragged note goes back to its original place and then jumps to the new location // @TODO: leftover comment from tibbits
            setVerseOrderOverrides(data);
            setIsDragging(false);
          }}
        />
      </View>
    </GameTemplate>
  );
};

class OrderTheVersesProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onCheckAnswer: () => void;
  onPass: (isCorrect: boolean, numVerses: number) => void;
  passage: any;
  subHeaderHeight: number;
};

export default OrderTheVerses;
